import type { AxiosInstance, AxiosRequestConfig } from 'axios';
import fallbackApi from '../fallbackApi';
import { type NftDetailsResponse, type NftListQuery, type NftListResponse } from '~/service/api/types';

export default (axiosInstance: AxiosInstance) => ({
    /**
   * @description get nft list by parameters
   *
   * @tags nft
   * @name NftList
   * @request GET:/collection/nfts/list/{collection}
   * @response `200` `NftListData` success getting collection
   * @response `default` `EmptyResponse` universal response is used for NoContent and AnyError answers, contains field Status as any content answers
   */
    async getNftList(collection: string, query?: NftListQuery) {
        const fallbackConfig = fallbackApi<NftListResponse>({
            text: 'Loading Nft collection',
            description: 'An error occurred while loading Nft collection',
            visible: 'error',
            exclude: [
                404,
                403
            ],
            fallback: {
                data: [],
                next: '',
                pagination: {
                    page: 0,
                    size: 0,
                    total: 0
                },
                status: {
                    code: 404,
                    message: 'Response fallbackApi get Nft List',
                    msgId: '1'
                }
            }
        });
        const config: AxiosRequestConfig = {
            params: {
                ...query
                    ? { ...query }
                    : {}
            },
            ...fallbackConfig
        };

        const res = await axiosInstance.get<NftListResponse>(
            `/v1/collections/nfts/list/${collection}`,
            config
        );

        return res.data;
    },

    /**
     * @description get nft info by collection and id
     *
     * @tags nft
     * @name LiteNft
     * @request GET:/collection/nfts/info/{collection}/{identifier}
     * @response `200` `NftDetailsData` success getting nft
     * @response `default` `EmptyResponse` universal response is used for NoContent and AnyError answers, contains field Status as any data answers
     */
    async getNftDetails(collection: string, identifier: string) {
        const fallbackConfig = fallbackApi<NftDetailsResponse>({
            text: 'Loading Nft details',
            description: 'An error occurred while loading Nft details',
            visible: 'error',
            exclude: [
                404,
                403
            ],
            fallback: {
                status: {
                    code: 404,
                    message: 'Response fallbackApi get Nft Details',
                    msgId: '1'
                }
            }
        });

        const config: AxiosRequestConfig = {
            ...fallbackConfig
        };

        const res = await axiosInstance.get<NftDetailsResponse>(
            `/v1/collections/nfts/info/${collection}/${identifier}`,
            config
        );

        return res.data;
    }
} as const);
